/**
 * GatewaySettingsSection — compact list of saved AI Gateway keys.
 *
 * Rendered inside the ProjectConfigGear popup. Shows a read-only summary
 * of each saved key. Add/edit opens GatewaySettingsModal.
 *
 * Feature tag: `gateway-settings`. NAD trace command:
 *
 *     scripts/nad trace --feature gateway-settings --last 10m
 */

import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Key, Plus, Trash2, Power, PowerOff, Pencil } from 'lucide-react'
import { cn } from '../../lib/utils'
import {
  getGatewayConfigs,
  updateGatewayConfig,
  deleteGatewayConfig,
  type GatewayConfig,
  type GatewayConfigKind,
} from '../../api/client'
import { notifyGatewayConfigChanged } from '../../hooks/useVideoModels'
import { notifyImageModelConfigChanged } from '../../hooks/useImageModels'
import { useCustomProtocolUiEnabled } from '../../hooks/useCustomProtocolUiEnabled'
import GatewaySettingsModal from './GatewaySettingsModal'
import { createNadDebug } from '../../utils/debugLog'
import type { ByomEntryPoint } from '../../analytics/generated/analyticsEvents'
import { track } from '../../analytics/events'

const log = createNadDebug('gateway-settings')

function notifyModelConfigsChanged(modelKind: GatewayConfigKind): void {
  if (modelKind === 'video') {
    notifyGatewayConfigChanged()
  } else if (modelKind === 'image') {
    notifyImageModelConfigChanged()
  }
}

function titleKey(modelKind: GatewayConfigKind): string {
  if (modelKind === 'director') return 'gatewaySection.myDirectorModel'
  if (modelKind === 'image') return 'gatewaySection.myImageModel'
  return 'gatewaySection.myVideoModel'
}

function addKey(modelKind: GatewayConfigKind): string {
  if (modelKind === 'director') return 'gatewaySection.addDirectorModel'
  if (modelKind === 'image') return 'gatewaySection.addImageModel'
  return 'gatewaySection.addModel'
}

interface GatewaySettingsSectionProps {
  showTitle?: boolean
  onModalOpenChange?: (isOpen: boolean) => void
  modelKind?: GatewayConfigKind
  onConfigsChanged?: () => void
  /**
   * Which BYOM surface hosts this list, for `byom_action`
   * (RFC-1577). Every production host passes it; without it no add_started is
   * logged.
   */
  analyticsEntryPoint?: ByomEntryPoint
}

export default function GatewaySettingsSection({
  showTitle = true,
  onModalOpenChange,
  modelKind = 'video',
  onConfigsChanged,
  analyticsEntryPoint,
}: GatewaySettingsSectionProps) {
  const { t } = useTranslation('settings')
  const customProtocolUiEnabled = useCustomProtocolUiEnabled()
  const [configs, setConfigs] = useState<GatewayConfig[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingConfig, setEditingConfig] = useState<GatewayConfig | null>(null)
  // All kinds, not just this list's: analytics context only.
  const [totalConfigCount, setTotalConfigCount] = useState<number | null>(null)

  const fetchConfigs = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await getGatewayConfigs()
      const filtered = result.filter((config) => (config.model_kind ?? 'video') === modelKind)
      log({
        event: 'section:fetch',
        trace_id: 'gateway-settings:section',
        count: filtered.length,
        model_kind: modelKind,
        custom_protocol_ui_enabled: customProtocolUiEnabled,
      })
      setConfigs(filtered)
      setTotalConfigCount(result.length)
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('gatewaySection.failedToLoadConfigs')
      log({ event: 'section:fetch-error', trace_id: 'gateway-settings:section', error: msg })
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }, [customProtocolUiEnabled, modelKind, t])

  useEffect(() => {
    fetchConfigs()
  }, [fetchConfigs])

  useEffect(() => {
    onModalOpenChange?.(modalOpen)
  }, [modalOpen, onModalOpenChange])

  const handleDelete = useCallback(async (id: string) => {
    log({ event: 'section:delete', trace_id: `gateway-settings:${id}:delete`, config_id: id })
    setError(null)
    try {
      await deleteGatewayConfig(id)
      setConfigs((prev) => prev.filter((c) => c.id !== id))
      notifyModelConfigsChanged(modelKind)
      onConfigsChanged?.()
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('gatewaySection.failedToDeleteConfig')
      log({
        event: 'section:delete-error',
        trace_id: `gateway-settings:${id}:delete`,
        config_id: id,
        error: msg,
      })
      setError(msg)
    }
  }, [modelKind, onConfigsChanged, t])

  const handleToggleEnabled = useCallback(async (config: GatewayConfig) => {
    log({
      event: 'section:toggle-enabled',
      trace_id: `gateway-settings:${config.id}:toggle-enabled`,
      config_id: config.id,
      current: config.enabled,
      next: !config.enabled,
      was_default: config.is_default,
      model_kind: config.model_kind ?? modelKind,
    })
    setError(null)
    try {
      const updated = await updateGatewayConfig(config.id, { enabled: !config.enabled })
      log({
        event: 'section:toggle-applied',
        trace_id: `gateway-settings:${config.id}:toggle-enabled`,
        config_id: config.id,
        enabled: updated.enabled,
        is_default: updated.is_default,
      })
      setConfigs((prev) => prev.map((c) => (c.id === config.id ? updated : c)))
      notifyModelConfigsChanged(config.model_kind ?? modelKind)
      onConfigsChanged?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('gatewaySection.failedToUpdateConfig'))
    }
  }, [modelKind, onConfigsChanged, t])

  const handleSetDefault = useCallback(async (config: GatewayConfig) => {
    if (config.is_default) return
    log({
      event: 'section:set-default',
      trace_id: `gateway-settings:${config.id}:set-default`,
      config_id: config.id,
    })
    setError(null)
    try {
      await updateGatewayConfig(config.id, { is_default: true })
      // Backend clears is_default on all other rows; re-fetch the full list
      await fetchConfigs()
      notifyModelConfigsChanged(config.model_kind ?? modelKind)
      onConfigsChanged?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('gatewaySection.failedToSetDefault'))
    }
  }, [fetchConfigs, modelKind, onConfigsChanged, t])

  const handleAddKey = useCallback(() => {
    log({ event: 'section:add-key', trace_id: 'gateway-settings:new:modal' })
    setEditingConfig(null)
    setModalOpen(true)
  }, [])

  const handleEditKey = useCallback((config: GatewayConfig) => {
    log({
      event: 'section:edit-key',
      trace_id: `gateway-settings:${config.id}:modal`,
      config_id: config.id,
    })
    setEditingConfig(config)
    setModalOpen(true)
  }, [])

  const handleModalSaved = useCallback(() => {
    setModalOpen(false)
    fetchConfigs()
    notifyModelConfigsChanged(modelKind)
    onConfigsChanged?.()
  }, [fetchConfigs, modelKind, onConfigsChanged])

  return (
    <div>
      {showTitle && (
        <h3 className="text-xxs font-normal uppercase tracking-wider text-muted-foreground mb-1">
          {t(titleKey(modelKind))}
        </h3>
      )}

      {error && (
        <div className="mb-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive">
          {error}
        </div>
      )}

      {isLoading && (
        <div className="text-xs font-medium text-muted-foreground py-2 text-center">{t('gatewaySection.loading')}</div>
      )}

      {!isLoading && configs.length > 0 && (
        <div className="space-y-1 mb-2">
          {configs.map((config) => (
            <div
              key={config.id}
              className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-lg border border-input-border bg-surface-1 px-2 py-2"
              data-testid={`gateway-config-${config.id}`}
            >
              <div className="flex min-w-0 basis-64 flex-1 flex-wrap items-center gap-1.5">
                <Key className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="text-xs font-medium text-foreground truncate block" title={config.key_name || t('gatewaySection.apiKeyFallback')}>{config.key_name || t('gatewaySection.apiKeyFallback')}</span>
                  {config.base_url && (
                    <span className="text-xxs text-muted-foreground font-mono truncate block" title={config.base_url}>{config.base_url}</span>
                  )}
                </div>
                <span className="text-xxs text-muted-foreground font-mono truncate">{config.api_key_masked}</span>
                {customProtocolUiEnabled && config.needs_api_key ? (
                  <span className="text-xxs text-warning bg-warning/10 px-1 py-0.5 rounded flex-shrink-0" data-testid={`needs-api-key-${config.id}`}>
                    {t('gatewaySection.needsApiKey')}
                  </span>
                ) : !config.enabled && (
                  <span className="text-xxs text-warning bg-warning/10 px-1 py-0.5 rounded flex-shrink-0">{t('gatewaySection.off')}</span>
                )}
              </div>
              <div className="ml-auto flex max-w-full flex-wrap items-center gap-0.5">
                {modelKind !== 'director' && (
                  <button
                    type="button"
                    onClick={() => handleSetDefault(config)}
                    disabled={!config.enabled}
                    aria-pressed={config.is_default}
                    className={cn(
                      'h-6 rounded-md border px-1.5 text-xxs font-medium transition-colors',
                      'focus:outline-none focus:ring-2 focus:ring-primary/20',
                      config.is_default
                        ? 'border-primary/50 bg-primary/15 text-primary cursor-default'
                        : config.enabled
                          ? 'border-input-border bg-surface-1 text-muted-foreground hover:border-input-border-hover hover:bg-surface-3 hover:text-foreground cursor-pointer'
                          : 'border-input-border bg-surface-1 text-text-muted opacity-50 cursor-not-allowed',
                    )}
                    title={config.is_default ? t('gatewaySection.default') : t('gatewaySection.setAsDefault')}
                    data-testid={`set-default-config-${config.id}`}
                  >
                    {t('gatewaySection.default')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleToggleEnabled(config)}
                  disabled={config.needs_api_key}
                  className={cn(
                    'h-6 w-6 flex items-center justify-center rounded-md border transition-colors',
                    'focus:outline-none focus:ring-2 focus:ring-primary/20',
                    config.enabled
                      ? 'border-success/30 bg-success/10 text-success hover:border-success/50 hover:bg-success/15 cursor-pointer'
                      : config.needs_api_key
                        ? 'border-input-border bg-surface-1 text-text-muted opacity-50 cursor-not-allowed'
                        : 'border-input-border bg-surface-1 text-muted-foreground hover:border-input-border-hover hover:bg-surface-3 hover:text-foreground',
                  )}
                  title={customProtocolUiEnabled && config.needs_api_key ? t('gatewaySection.needsApiKey')
                    : config.enabled ? t('gatewaySection.disable') : t('gatewaySection.enable')}
                  aria-label={customProtocolUiEnabled && config.needs_api_key ? t('gatewaySection.needsApiKey')
                    : config.enabled ? t('gatewaySection.disable') : t('gatewaySection.enable')}
                  data-testid={`toggle-config-${config.id}`}
                >
                  {config.enabled ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
                </button>
                <button
                  type="button"
                  onClick={() => handleEditKey(config)}
                  className="h-6 w-6 flex items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-surface-3 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer"
                  title={t('gatewaySection.edit')}
                  data-testid={`edit-config-${config.id}`}
                >
                  <Pencil className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (analyticsEntryPoint !== undefined) {
                      track('byom_action', { action: 'delete_clicked', model_type: config.model_kind ?? modelKind, entry_point: analyticsEntryPoint })
                    }
                    void handleDelete(config.id)
                  }}
                  className="h-6 w-6 flex items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-destructive/20 cursor-pointer"
                  title={t('gatewaySection.delete')}
                  data-testid={`delete-config-${config.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={handleAddKey}
        className={cn(
          'h-9 w-full flex items-center justify-center gap-1 rounded-lg border border-input-border bg-surface-1',
          'text-xs font-medium text-muted-foreground transition-colors hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground',
          'focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer',
        )}
        data-testid={modelKind === 'video' ? 'add-gateway-button' : `add-${modelKind}-gateway-button`}
      >
        <Plus className="w-3.5 h-3.5" />
        {t(addKey(modelKind))}
      </button>

      <GatewaySettingsModal
        isOpen={modalOpen}
        onClose={() => { log({ event: 'modal:close', trace_id: `gateway-settings:${editingConfig?.id ?? 'new'}:modal` }); setModalOpen(false) }}
        onSaved={handleModalSaved}
        editConfig={editingConfig}
        modelKind={modelKind}
        addStartedAnalytics={
          analyticsEntryPoint === undefined
            ? undefined
            : { entryPoint: analyticsEntryPoint, configuredModelCount: totalConfigCount }
        }
      />
    </div>
  )
}
